import type { LicenseAnswers } from "@/types/license";

export type PartyContactOption = {
  party_id?: string | null;
  party?: {
    company_name?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipcode?: string | null;
  } | null;
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizeCompanyKey(value: string | null | undefined): string {
  return (value ?? "").trim().replace(/\s+/g, " ").toUpperCase();
}

export function isEmptyUnregisteredAnswer(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).every((entry) =>
      isEmptyUnregisteredAnswer(entry),
    );
  }
  return false;
}

export function contactAnswerFromPartyOption(
  option: PartyContactOption | null | undefined,
): Record<string, string> | null {
  const partyId = option?.party_id;
  if (!partyId) return null;
  const party = option.party;
  return {
    partyId,
    name: party?.name?.trim() ?? "",
    companyName: party?.company_name?.trim() ?? "",
    email: party?.email?.trim() ?? "",
    phone: party?.phone?.trim() ?? "",
    addressLine1: party?.address1?.trim() ?? "",
    addressLine2: party?.address2?.trim() ?? "",
    city: party?.city?.trim() ?? "",
    state: party?.state?.trim() ?? "",
    zipCode: party?.zipcode?.trim() ?? "",
  };
}

export type PartyRecord = {
  id: string;
  company_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
};

/**
 * Resolve a party id to a contact option, preferring the question's configured
 * options and falling back to the party record when it is not among them.
 */
export function partyContactOptionFor(
  partyId: string | null | undefined,
  options: readonly PartyContactOption[],
  party: PartyRecord | null | undefined,
): PartyContactOption | null {
  if (!partyId) return null;
  const configured = options.find((option) => option.party_id === partyId);
  if (configured) return configured;
  if (party?.id === partyId) {
    return { party_id: party.id, party };
  }
  return null;
}

export function partyIdFromUnregisteredAnswer(
  raw: unknown,
  options: readonly PartyContactOption[] = [],
): string | null {
  if (typeof raw === "string" && UUID_RE.test(raw.trim())) {
    return raw.trim();
  }
  if (raw && typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    const nested =
      (typeof record.partyId === "string" && record.partyId) ||
      (typeof record.party_id === "string" && record.party_id) ||
      null;
    if (nested && UUID_RE.test(nested)) return nested;
    const company = normalizeCompanyKey(
      typeof record.companyName === "string"
        ? record.companyName
        : typeof record.company_name === "string"
          ? record.company_name
          : typeof record.name === "string"
            ? record.name
            : null,
    );
    if (company) {
      for (const option of options) {
        const optionKey = normalizeCompanyKey(
          option.party?.company_name || option.party?.name,
        );
        if (option.party_id && optionKey === company) {
          return option.party_id;
        }
      }
    }
  }
  return null;
}

export function answersDictFromLicenseAnswers(
  answers: Partial<LicenseAnswers> | Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!answers) return null;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(answers)) {
    if (key === "effective_date") continue;
    if (isEmptyUnregisteredAnswer(value)) continue;
    result[key] = value;
  }
  return Object.keys(result).length > 0 ? result : null;
}

export type UnregisteredSelectQuestion = {
  id: string;
  question: string;
  answer_type?: string | null;
  question_type?: string | null;
  answer_options?: Record<string, unknown> | null;
  answer_option_keys_order?: string[] | null;
};

/** Defaults applied when creating an unregistered property from case review. */
export const FORECLOSURE_UNREGISTERED_SELECT_DEFAULTS: ReadonlyArray<{
  questionMatchers: readonly RegExp[];
  optionMatchers: readonly RegExp[];
}> = [
  {
    questionMatchers: [/foreclosure\s+status/i],
    optionMatchers: [/public\s*nod/i],
  },
  {
    questionMatchers: [/occupancy\s+status/i],
    optionMatchers: [/^unknown$/i],
  },
];

export function findRegistrationQuestionId(
  questions: readonly UnregisteredSelectQuestion[],
  matchers: readonly RegExp[],
): string | null {
  for (const question of questions) {
    if (
      question.question_type != null &&
      question.question_type !== "registration"
    ) {
      continue;
    }
    if (
      question.answer_type != null &&
      question.answer_type !== "single_select"
    ) {
      continue;
    }
    const text = question.question ?? "";
    if (matchers.some((matcher) => matcher.test(text))) {
      return question.id;
    }
  }
  return null;
}

export function findAnswerOptionKey(
  question: UnregisteredSelectQuestion | null | undefined,
  matchers: readonly RegExp[],
): string | null {
  const options = question?.answer_options;
  if (!options || typeof options !== "object") return null;
  const keys = question.answer_option_keys_order?.length
    ? [
        ...question.answer_option_keys_order.filter((key) => key in options),
        ...Object.keys(options).filter(
          (key) => !question.answer_option_keys_order?.includes(key),
        ),
      ]
    : Object.keys(options);
  for (const key of keys) {
    if (matchers.some((matcher) => matcher.test(key))) {
      return key;
    }
  }
  return null;
}

export function buildUnregisteredInitialAnswers(params: {
  omtQuestionId?: string | null;
  amsQuestionId?: string | null;
  caseNumberQuestionId?: string | null;
  omtOption?: PartyContactOption | null;
  amsOption?: PartyContactOption | null;
  caseNumber?: string | null;
  existingAnswers?: Record<string, unknown> | null;
  questions?: readonly UnregisteredSelectQuestion[] | null;
  selectDefaults?: typeof FORECLOSURE_UNREGISTERED_SELECT_DEFAULTS;
}): Partial<LicenseAnswers> {
  const answers: Record<string, unknown> = {
    ...(params.existingAnswers ?? {}),
  };
  const omtAnswer = contactAnswerFromPartyOption(params.omtOption);
  if (
    params.omtQuestionId &&
    omtAnswer &&
    answers[params.omtQuestionId] == null
  ) {
    answers[params.omtQuestionId] = omtAnswer;
  }
  // Prefer AMS suggestion; if missing, reuse OMT so case-review create still
  // fills the AMS company-contact fields.
  const amsAnswer =
    contactAnswerFromPartyOption(params.amsOption) ??
    (params.amsQuestionId && params.amsQuestionId !== params.omtQuestionId
      ? omtAnswer
      : null);
  if (
    params.amsQuestionId &&
    amsAnswer &&
    answers[params.amsQuestionId] == null
  ) {
    answers[params.amsQuestionId] = amsAnswer;
  }
  const caseNumber = params.caseNumber?.trim();
  if (
    params.caseNumberQuestionId &&
    caseNumber &&
    answers[params.caseNumberQuestionId] == null
  ) {
    answers[params.caseNumberQuestionId] = caseNumber;
  }

  const selectDefaults =
    params.selectDefaults ?? FORECLOSURE_UNREGISTERED_SELECT_DEFAULTS;
  const questions = params.questions ?? [];
  for (const rule of selectDefaults) {
    const questionId = findRegistrationQuestionId(
      questions,
      rule.questionMatchers,
    );
    if (!questionId || answers[questionId] != null) continue;
    const question = questions.find((item) => item.id === questionId);
    const optionKey = findAnswerOptionKey(question, rule.optionMatchers);
    if (optionKey) {
      answers[questionId] = optionKey;
    }
  }

  return answers as Partial<LicenseAnswers>;
}

export function foreclosureCaseSuggestionWriteBack(params: {
  selectedOmtPartyId: string | null;
  selectedAmsPartyId: string | null;
  selectedRegistrantAccountId: string | null;
  currentOmtPartyId: string | null;
  currentAmsPartyId: string | null;
  currentRegistrantAccountId: string | null;
}): {
  overrideOmtPartyId?: string | null;
  overrideAmsPartyId?: string | null;
  overrideRegistrantAccountId?: string | null;
} {
  const body: {
    overrideOmtPartyId?: string | null;
    overrideAmsPartyId?: string | null;
    overrideRegistrantAccountId?: string | null;
  } = {};
  if (params.selectedOmtPartyId !== params.currentOmtPartyId) {
    body.overrideOmtPartyId = params.selectedOmtPartyId;
  }
  if (params.selectedAmsPartyId !== params.currentAmsPartyId) {
    body.overrideAmsPartyId = params.selectedAmsPartyId;
  }
  if (
    params.selectedRegistrantAccountId !== params.currentRegistrantAccountId
  ) {
    body.overrideRegistrantAccountId = params.selectedRegistrantAccountId;
  }
  return body;
}
